import { request } from "./http";

export interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_verified: boolean;
  is_superuser: boolean;
  totp_enabled: boolean;
  kyc_status: string;
  last_login_at: string | null;
  created_at: string;
  roles: string[];
}

export interface OrgRow {
  id: string;
  slug: string;
  name: string;
  plan: string;
  region: string;
  status: string;
  primary_domain?: string | null;
  created_at?: string | null;
  // Phase 10A.1 — licensed-entity attribution.
  licensed_entity_name?: string | null;
  jurisdiction?: string | null;
  regulatory_authority?: string | null;
  license_number?: string | null;
  compensation_scheme?: string | null;
  complaints_email?: string | null;
  terms_url?: string | null;
  privacy_url?: string | null;
  risk_warning_html?: string | null;
}

export interface OrgCreate {
  slug: string;
  name: string;
  plan?: string;
  region?: string;
  status?: "active" | "disabled" | "suspended";
  primary_domain?: string;
}

export interface OrgPatch {
  name?: string;
  plan?: string;
  region?: string;
  status?: "active" | "disabled" | "suspended";
  primary_domain?: string | null;
  // Phase 10A.1 — compliance edits. Set to empty string to clear; null
  // is rejected by the backend Pydantic model.
  licensed_entity_name?: string | null;
  jurisdiction?: string | null;
  regulatory_authority?: string | null;
  license_number?: string | null;
  compensation_scheme?: "FSCS" | "ICF" | "SIPC" | "EDB" | "NONE" | null;
  complaints_email?: string | null;
  terms_url?: string | null;
  privacy_url?: string | null;
  risk_warning_html?: string | null;
}

export interface OrgUserCreate {
  email: string;
  password: string;
  full_name?: string;
  is_superuser?: boolean;
}

export interface OrgUserCreated {
  id: string;
  email: string;
  is_superuser: boolean;
}

export interface OIDCSettings {
  issuer: string | null;
  client_id: string | null;
  client_secret_set: boolean;
  scopes: string[];
  redirect_uri: string | null;
}

export interface OIDCSettingsBody {
  issuer?: string | null;
  client_id?: string | null;
  client_secret?: string | null;
  scopes?: string[] | null;
  redirect_uri?: string | null;
}

export interface KYCSettings {
  providers: Record<string, { secret_set: boolean }>;
}

export interface PermissionRow {
  id: string;
  key: string;
  description: string | null;
}

export interface RoleRow {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  permission_keys: string[];
}

export interface RoleCreate {
  name: string;
  description?: string | null;
  permission_keys?: string[];
}

export type RolePatch = Partial<RoleCreate>;

export interface UserPatch {
  is_active?: boolean;
  email?: string;
  full_name?: string | null;
  is_verified?: boolean;
}

export const adminUsers = {
  list: (limit = 200) => request<AdminUser[]>(`/api/v1/admin/users?limit=${limit}`),
  setActive: (id: string, is_active: boolean) =>
    request<AdminUser>(`/api/v1/admin/users/${id}`, {
      method: "PATCH",
      body: { is_active },
    }),
  patch: (id: string, body: UserPatch) =>
    request<AdminUser>(`/api/v1/admin/users/${id}`, {
      method: "PATCH",
      body,
    }),
  assignRole: (user_id: string, role_id: string) =>
    request<void>(`/api/v1/admin/users/${user_id}/roles`, { body: { role_id } }),
  revokeRole: (user_id: string, role_id: string) =>
    request<void>(`/api/v1/admin/users/${user_id}/roles/${role_id}`, { method: "DELETE" }),
};

export const adminRoles = {
  list: () => request<RoleRow[]>("/api/v1/admin/roles"),
  create: (body: RoleCreate) => request<RoleRow>("/api/v1/admin/roles", { body }),
  patch: (id: string, body: RolePatch) =>
    request<RoleRow>(`/api/v1/admin/roles/${id}`, { method: "PATCH", body }),
  remove: (id: string) =>
    request<void>(`/api/v1/admin/roles/${id}`, { method: "DELETE" }),
  permissions: () => request<PermissionRow[]>("/api/v1/admin/permissions"),
};

export const adminOrgs = {
  list: () => request<OrgRow[]>("/api/v1/admin/orgs"),
  create: (body: OrgCreate) => request<OrgRow>("/api/v1/admin/orgs", { body }),
  patch: (id: string, body: OrgPatch) =>
    request<OrgRow>(`/api/v1/admin/orgs/${id}`, { method: "PATCH", body }),
  disable: (id: string) =>
    request<void>(`/api/v1/admin/orgs/${id}`, { method: "DELETE" }),
  bootstrapUser: (id: string, body: OrgUserCreate) =>
    request<OrgUserCreated>(`/api/v1/admin/orgs/${id}/users`, { body }),
};

export const oidcConfig = {
  get: () => request<OIDCSettings>("/api/v1/organizations/me/oidc"),
  save: (body: OIDCSettingsBody) =>
    request<OIDCSettings>("/api/v1/organizations/me/oidc", { method: "PUT", body }),
};

export const kycConfig = {
  get: () => request<KYCSettings>("/api/v1/organizations/me/kyc"),
  setProviderSecret: (provider: string, secret: string | null) =>
    request<KYCSettings>(`/api/v1/organizations/me/kyc/${provider}`, {
      method: "PUT",
      body: { secret },
    }),
};
